import { Region, VatStatus } from "Infrastructure/Api/Api";
import { Resources, type TFunction } from "Translations/Resources";

// Everything the app knows about the VAT ID (IČ DPH) per market lives here, so adding a Region means
// one file. Both records are typed as complete, so a new Region member fails to compile until decided.

// The persisted "no VAT ID" value — PartyDto.vatNumber is a required string, and a natural person or an
// unregistered business carries this rather than null. Whether a party is registered is PartyDto.vatStatus.
export const NO_VAT_NUMBER = "";

// Which markets publish the VAT ID in their business register: ARES does; the Slovak registers don't, so an
// SK form shows the field — filled from VIES ("SK" + DIČ) when it knows the number, typed otherwise.
export const IS_VAT_NUMBER_FROM_REGISTRY: Record<Region, boolean> = {
  [Region.CZ]: true,
  [Region.SK]: false,
};

// Shape per market, mirroring the backend TaxIdentifierFormat: "CZ" + 8–10 digits, "SK" + 10 digits.
export const VAT_NUMBER_PATTERN: Record<Region, RegExp> = {
  [Region.CZ]: /^CZ\d{8,10}$/,
  [Region.SK]: /^SK\d{10}$/,
};

// The same normalization the backend applies before validating: no whitespace, upper case.
export const normalizeVatNumber = (value: string): string =>
  value.replace(/\s+/g, "").toUpperCase();

type VatNumberValues = {
  // The market whose shape applies: picked inline during onboarding, stored on the party afterwards.
  region: Region;
  vatNumber: string;
};

// The register has not spoken about the numbers: an outage, or no DIČ to derive the IČ DPH from. The
// onboarding form then says so under the field instead of showing a status.
export const isVatQuestionOpen = (vatRegistryStatus: VatStatus): boolean =>
  vatRegistryStatus === VatStatus.Unknown;

// A number is on file (typed or stored) but the register does not know it — most likely a typo. The number
// is kept (it is an identifier), the status says it does not count.
export const isVatNumberRejectedByRegistry = (
  vatRegistryStatus: VatStatus,
  vatNumber: string,
): boolean =>
  vatRegistryStatus === VatStatus.NotRegistered && vatNumber !== NO_VAT_NUMBER;

/**
 * The one VAT ID rule every form applies, as a message or null: an empty number is always allowed (whether
 * the party is registered is the register's word, never the form's), a typed one must have the market's
 * shape. Callers turn it into a zod issue on their own `vatNumber` path — a shared refinement would have to
 * know each schema's shape. The backend re-checks it, but its failure would only surface as a generic save error.
 */
export const getVatNumberError = (
  t: TFunction,
  { region, vatNumber }: VatNumberValues,
): string | null => {
  if (vatNumber === NO_VAT_NUMBER) {
    return null;
  }

  if (!VAT_NUMBER_PATTERN[region].test(vatNumber)) {
    return t(Resources.Validation.InvalidVatNumber);
  }

  return null;
};
